/**
 * Screenshots of what the run saw on this page, in the order they were taken.
 *
 * `before` and `after` bracket an ordinary fill; a page that paused shows
 * `pause` and `resume` too, and the difference between those two is usually
 * the answer to "why did OneClick get stuck here" — it is what the pro had to
 * change. Drake Desktop adds unlabelled heartbeat frames every few seconds in
 * between, so a desktop page reads as a filmstrip rather than a pair.
 *
 * A frame with no image shows its recorded reason rather than nothing at all:
 * "we deliberately skipped this" and "capture failed" are different findings,
 * and the previous version of this feature could tell nobody which was which.
 * An expired frame had an image that S3's retention rule has since deleted.
 */

import React from "react";
import {
  Anchor,
  Badge,
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { TimestepScreenshot, TimestepScreenshotPhase } from "../../types";

interface ScreenshotsProps {
  screenshots: TimestepScreenshot[];
}

/** Human label for a capture phase. */
const phaseLabel = (phase: TimestepScreenshotPhase): string => {
  switch (phase) {
    case "before":
      return "Before fill";
    case "after":
      return "After fill";
    case "pause":
      return "Paused here";
    case "resume":
      return "After the pro fixed it";
    case "stop":
      return "Manual stop";
    case "entry":
      return "Where the run started";
    case "login":
      return "Waiting on sign-in";
    case "search":
      return "Looking the client up";
    case "create":
      return "Creating the return";
    case "opened":
      return "Return open";
    case "failed":
      return "Where setup gave up";
    default: {
      const unhandled: never = phase;
      return unhandled;
    }
  }
};

/** A frame's heading: its phase, or its capture time for a heartbeat frame. */
const frameLabel = (shot: TimestepScreenshot): string => {
  if (shot.phase) return phaseLabel(shot.phase);
  const time = shot.capturedAt
    ? new Date(shot.capturedAt).toLocaleTimeString()
    : `#${shot.sequence}`;
  return `Heartbeat · ${time}`;
};

export const Screenshots: React.FC<ScreenshotsProps> = ({ screenshots }) => {
  if (!screenshots?.length) return null;

  const ordered = [...screenshots].sort((a, b) => a.sequence - b.sequence);

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Title order={4}>Screenshots</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {ordered.map((shot) => (
            <Stack key={shot.id} gap="xs">
              <Group gap="xs">
                <Text fw={shot.phase ? 600 : 400} size="sm">
                  {frameLabel(shot)}
                </Text>
                {!shot.imageUrl && (
                  <Badge color="gray" variant="light" size="sm">
                    {shot.expired
                      ? "no screenshot · expired"
                      : (shot.skipReason ?? "no image")}
                  </Badge>
                )}
              </Group>
              {shot.expiredNote && (
                <Text size="xs" c="dimmed">
                  {shot.expiredNote}
                </Text>
              )}
              {shot.imageUrl && (
                <>
                  <Image
                    src={shot.imageUrl}
                    alt={`${frameLabel(shot)} screenshot`}
                    radius="sm"
                    fit="contain"
                  />
                  {/* The presigned URL expires in 30 minutes, so this opens the
                      full-size image now rather than being a durable link. */}
                  <Anchor href={shot.imageUrl} target="_blank" size="xs">
                    Open full size
                  </Anchor>
                </>
              )}
            </Stack>
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
};
